/**
 * Rate Limiting for Authentication
 * Addresses BUG-H002 (No rate limiting on login)
 */

interface LoginAttempt {
  count: number;
  firstAttempt: number;
  blockedUntil?: number;
}

// Store login attempts in memory (client-side protection)
// Server-side protection should be handled by Supabase Auth or edge functions
const loginAttempts = new Map<string, LoginAttempt>();

// Configuration
const MAX_ATTEMPTS = 5;
const WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const BLOCK_DURATION_MS = 15 * 60 * 1000; // Block for 15 minutes after max attempts

/**
 * Records a failed login attempt
 */
export function recordFailedLogin(identifier: string): void {
  const now = Date.now();
  const existing = loginAttempts.get(identifier);

  if (!existing) {
    loginAttempts.set(identifier, {
      count: 1,
      firstAttempt: now
    });
    return;
  }

  // If the window has expired, reset
  if (now - existing.firstAttempt > WINDOW_MS) {
    loginAttempts.set(identifier, {
      count: 1,
      firstAttempt: now
    });
    return;
  }

  // Increment count
  existing.count++;

  // If max attempts reached, block the user
  if (existing.count >= MAX_ATTEMPTS) {
    existing.blockedUntil = now + BLOCK_DURATION_MS;
  }

  loginAttempts.set(identifier, existing);
}

/**
 * Checks if login should be blocked
 */
export function isLoginBlocked(identifier: string): {
  blocked: boolean;
  remainingAttempts?: number;
  blockedUntil?: Date;
  message?: string;
} {
  const now = Date.now();
  const attempt = loginAttempts.get(identifier);

  if (!attempt) {
    return { blocked: false, remainingAttempts: MAX_ATTEMPTS };
  }

  // Check if currently blocked
  if (attempt.blockedUntil && now < attempt.blockedUntil) {
    const minutesRemaining = Math.ceil((attempt.blockedUntil - now) / 60000);
    return {
      blocked: true,
      blockedUntil: new Date(attempt.blockedUntil),
      message: `Too many failed login attempts. Please try again in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}.`
    };
  }

  // Check if window has expired
  if (now - attempt.firstAttempt > WINDOW_MS) {
    loginAttempts.delete(identifier);
    return { blocked: false, remainingAttempts: MAX_ATTEMPTS };
  }

  // Calculate remaining attempts
  const remaining = MAX_ATTEMPTS - attempt.count;

  if (remaining <= 0) {
    // Should be blocked but blockedUntil wasn't set somehow - set it now
    attempt.blockedUntil = now + BLOCK_DURATION_MS;
    loginAttempts.set(identifier, attempt);
    return {
      blocked: true,
      blockedUntil: new Date(attempt.blockedUntil),
      message: 'Too many failed login attempts. Please try again in 15 minutes.'
    };
  }

  return {
    blocked: false,
    remainingAttempts: remaining,
    message: remaining <= 2 ? `Warning: ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before temporary lockout.` : undefined
  };
}

/**
 * Clears login attempts on successful login
 */
export function clearLoginAttempts(identifier: string): void {
  loginAttempts.delete(identifier);
}

/**
 * Gets the identifier for rate limiting (email or IP)
 * In browser, we use email. Server-side should also check IP.
 */
export function getLoginIdentifier(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Cleanup old entries periodically
 */
export function cleanupOldAttempts(): void {
  const now = Date.now();
  for (const [key, attempt] of loginAttempts.entries()) {
    // Remove entries older than the window
    if (now - attempt.firstAttempt > WINDOW_MS * 2) {
      loginAttempts.delete(key);
    }
    // Remove unblocked entries
    if (attempt.blockedUntil && now > attempt.blockedUntil) {
      loginAttempts.delete(key);
    }
  }
}

// Run cleanup every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(cleanupOldAttempts, 5 * 60 * 1000);
}
